import type { ReactNode } from 'react'
import { Link } from 'react-router'
import type { Alarm, Station } from '../api/types'
import { VENDOR_LABEL, externalIdLabel } from '../api/types'
import { formatCount } from '../lib/format'
import { formatDateTime, offsetFor, relativeDays } from '../lib/time'
import { duration } from '../lib/health'
import { statusTone, statusWord, systemStatus } from '../lib/freshness'
import { checksStale } from '../lib/health'
import { AlarmPill, StationAlarms } from './Alarms'
import { CollectNow } from './CollectNow'

/** How far the archive is from yesterday: `days_behind`, which the daily
 *  run's whole job is keeping at zero. Moved here from the old station card
 *  on Home with every other fact it carried. */
function archiveState(station: Station): { label: string; tone: string } {
  if (!station.active) return { label: 'decommissioned', tone: 'neutral' }
  if (station.days_behind === null) return { label: 'no data', tone: 'bad' }
  if (station.days_behind <= 0) return { label: 'up to date', tone: 'good' }
  if (station.days_behind === 1) return { label: '1 day behind', tone: 'warn' }
  return { label: `${station.days_behind} days behind`, tone: 'bad' }
}

/** "UTC+4", from the zone's own offset rather than a literal. */
export function utcLabel(): string {
  const offset = offsetFor()
  const [, sign, hours, minutes] = offset.match(/^([+-])(\d{2}):(\d{2})$/) ?? []
  if (!sign) return 'UTC'
  return `UTC${sign}${Number(hours)}${minutes === '00' ? '' : `:${minutes}`}`
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return <div><dt>{label}</dt><dd>{children}</dd></div>
}

function Reading({ label, at }: { label: string; at: string | null }) {
  return (
    <Row label={label}>
      {at
        ? <><span className="mono">{formatDateTime(at)}</span> <span className="muted">({relativeDays(at)})</span></>
        : <span className="muted">None</span>}
    </Row>
  )
}

interface Props {
  station: Station
  /** The station picker, which sits in the band on the right. */
  picker: ReactNode
  /** Its open alarms (phase 4); undefined while they load. */
  alarms?: Alarm[]
}

/** The compact reading header keeps station identity and current alarm
 *  status visible. Archive and transmission facts are in StationMetadata,
 *  in Sensor Info's Properties. `station.alerts` should be the latest
 *  polled values. */
export function StationHeader({ station, picker, alarms }: Props) {
  const status = systemStatus(station, checksStale(station.health_checked_at))
  return (
    <div className="station-band station-band-compact">
      <div className="station-band-main">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to="/overview">Overview</Link> / {VENDOR_LABEL[station.vendor]}
        </nav>
        <div className="station-band-title">
          <h1>{station.name}</h1>
          <span className={`pill status-${statusTone(status)}`} title={status.note || undefined}>
            {statusWord(status)}
          </span>
          {alarms && <AlarmPill alarms={alarms} />}
        </div>
        {alarms && <StationAlarms alarms={alarms} />}
        <div className="station-reading-links">
          <Link to={`/sensors/info/${encodeURIComponent(station.code)}`}>Sensor Info</Link>
          <Link to="/analysis/new">Create analysis</Link>
        </div>
        {/* A gateway or MDS station only; the component asks the server and
            draws nothing for a station that cannot be asked. */}
        {station.active && station.vendor !== 'mrl_push' && <CollectNow station={station} />}
      </div>
      <div className="station-band-picker">{picker}</div>
    </div>
  )
}

/** Transmission and archive metadata belong with the instrument record: rows
 *  of the Properties card on Sensor Info, where the owner asked for them
 *  rather than in a fold of their own (review of 2026-10-22). The times are
 *  station-local; the station band above them says which zone. */
export function StationMetadata({ station }: { station: Station }) {
  const archive = archiveState(station)
  return (
    <dl className="props">
      <Row label="Code"><span className="mono">{station.code}</span></Row>
      <Row label={externalIdLabel(station.vendor)}><span className="mono">{station.external_id}</span></Row>
      <Row label="Archive"><span className={`pill ${archive.tone}`}>{archive.label}</span></Row>
      <Reading label="First reading" at={station.first_measurement_at} />
      <Reading label="Last reading" at={station.last_measurement_at} />
      <Row label="Logger interval"><span className="mono">{duration(station.poll_interval_s * 1000)}</span></Row>
      <Row label="Collected daily at">
        <span className="mono">{station.schedule_time.slice(0, 5)}</span>
        {!station.schedule_enabled && <> <span className="pill warn">off</span></>}
      </Row>
      <Row label="Mapped channels">
        <span className="mono">{formatCount(station.mapped_channels)}</span>
        {/* An unmapped channel is data being discarded on every run, so
            it belongs where the station is read, not in an admin screen. */}
        {station.unmapped_channels > 0 && (
          <> <span className="pill warn">{station.unmapped_channels} unmapped</span></>
        )}
      </Row>
      <Row label="Days failed / empty">
        <span className="mono">{formatCount(station.failed_days)} / {formatCount(station.empty_days)}</span>
      </Row>
    </dl>
  )
}
